import React from 'react';
import { UseFormRegister, FieldErrors } from 'react-hook-form';
import { InvoiceHire } from '../../types';
import Input from './Input';
import Select from './Select';
import { findCodePrefix } from '../../utils/codePrefixUtils';
import { resolveCurrencyCode } from '../../utils/currencyUtils';

interface SelectOption {
  value: string;
  label: string;
}

interface InvoiceHireHeaderSectionProps {
  register: UseFormRegister<InvoiceHire>;
  errors: FieldErrors<InvoiceHire>;
  invoiceHire?: InvoiceHire;
  invoiceTypeOptions: SelectOption[];
  modeOfPayOptions: SelectOption[];
  statusOptions: SelectOption[];
  clientOptions: SelectOption[];
  vesselOptions: SelectOption[];
  currencyOptions: SelectOption[];
  foaOptions: SelectOption[];
  codePrefixes: any[];
  selectedCodePrefix: string;
  invoiceSeqNumber: string;
  onCodePrefixChange: (prefixId: string) => void;
  selectedFoaId: string | undefined;
  foas: any[];
}

const InvoiceHireHeaderSection: React.FC<InvoiceHireHeaderSectionProps> = ({
  register,
  errors,
  invoiceHire,
  invoiceTypeOptions,
  modeOfPayOptions,
  statusOptions,
  clientOptions,
  vesselOptions,
  currencyOptions,
  foaOptions,
  codePrefixes,
  selectedCodePrefix,
  invoiceSeqNumber,
  onCodePrefixChange,
  selectedFoaId,
  foas,
}) => {
  return (
    <>
      {/* Header Section */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold text-gray-900">Petromarine Offshore Projects Support Co.</h2>
          <p className="text-sm text-gray-600">INVOICE</p>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <Select
                label="Invoice Type"
                {...register('type', { required: 'Invoice type is required' })}
                options={invoiceTypeOptions}
                error={errors.type?.message}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Invoice Number <span className="text-red-500 ml-1">*</span>
              </label>
              <select
                value={selectedCodePrefix}
                onChange={(e) => onCodePrefixChange(e.target.value)}
                className={`form-select ${invoiceHire ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                disabled={!!invoiceHire}
              >
                <option value="">Select Code Prefix</option>
                {codePrefixes.map((prefix: any) => (
                  <option key={prefix.id} value={prefix.id}>
                    {prefix.prefix} - {prefix.name || prefix.description || ''}
                  </option>
                ))}
              </select>
              {selectedCodePrefix && (
                <div className="mt-2 p-2 bg-gray-50 rounded-md border border-gray-200">
                  <span className="text-sm text-gray-600">Generated Code: </span>
                  <span className="text-sm font-semibold text-primary-600">
                    {findCodePrefix(codePrefixes, selectedCodePrefix)?.prefix}{invoiceSeqNumber}
                  </span>
                </div>
              )}
              <input type="hidden" {...register('invoiceNumber', { required: 'Invoice number is required' })} />
              {errors.invoiceNumber?.message && (
                <p className="mt-1 text-sm text-red-600">{errors.invoiceNumber.message}</p>
              )}
            </div>
            <div>
              <Select
                label="Mode of Pay"
                {...register('modeOfPay', { required: 'Mode of pay is required' })}
                options={modeOfPayOptions}
                error={errors.modeOfPay?.message}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Invoice Details */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold text-gray-900">Invoice Details</h2>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <Input
                label="Invoice Date"
                type="date"
                {...register('invoiceDate', { required: 'Invoice date is required' })}
                error={errors.invoiceDate?.message}
              />
            </div>
            <div>
              <Select
                label="Customer"
                {...register('customerId', { required: 'Customer is required' })}
                options={clientOptions}
                error={errors.customerId?.message}
              />
            </div>
            <div>
              <Input
                label="Contract No."
                {...register('contractNumber')}
                placeholder="Contract number (optional)"
              />
            </div>
            <div>
              <Input
                label="Operation Date (Month/Year)"
                type="month"
                {...register('operationDate', { required: 'Operation date is required' })}
                error={errors.operationDate?.message}
              />
            </div>
            <div>
              <Input
                label="Project"
                {...register('projectId')}
                placeholder="Project ID or name"
              />
            </div>
            <div>
              <Input
                label="Tel."
                {...register('telephone')}
                placeholder="Telephone number"
              />
            </div>
            <div>
              <Select
                label="Vessel"
                {...register('vesselId')}
                options={vesselOptions}
              />
            </div>
            <div>
              <Select
                label="Status"
                {...register('status', { required: 'Status is required' })}
                options={statusOptions}
                error={errors.status?.message}
              />
            </div>
            <div>
              <Select
                label="Currency"
                {...register('currency', { required: 'Currency is required' })}
                options={currencyOptions}
                error={errors.currency?.message}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
            <div>
              <Select
                label="FOA (امر الاسناد)"
                {...register('foaId')}
                options={foaOptions}
              />
            </div>
            {selectedFoaId && foas.length > 0 && (() => {
              const selectedFoa = foas.find((f: any) => (f.documentId || f.id) === selectedFoaId);
              if (!selectedFoa) return null;
              // currency may be a populated relation object; never render it directly
              const firstAmount = selectedFoa.amounts?.[0];
              const foaCurrency = firstAmount?.currency
                || resolveCurrencyCode(selectedFoa.currency1 ?? selectedFoa.currency);
              const foaAmount = firstAmount?.amount ?? selectedFoa.amount1 ?? selectedFoa.amount ?? 0;
              return (
                <div className="md:col-span-2 flex items-end">
                  <div className="bg-blue-50 p-3 rounded-lg w-full">
                    <p className="text-sm text-blue-800">
                      <strong>FOA:</strong> {selectedFoa.foaNumber} - {selectedFoa.englishName} |
                      Amount: {foaCurrency} {Number(foaAmount).toLocaleString()}
                    </p>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      </div>
    </>
  );
};

export default InvoiceHireHeaderSection;
